import React, { useState, useMemo } from 'react';
import { Stack, Paper, Box } from '@mui/material';
import { Button, Text, rem, success, error, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import { WEAVE_FONT } from '@Utils/fonts';
import { CurveMethodRadioGroup } from './CurveMethodRadioGroup';
import { useCurveConfig } from './hooks';
import { useDispatch } from 'react-redux';
import { updateModelCurveConfigs } from '@Services/deteriorationModels.services';
import {
	listDeteriorationModelCurveConfigsAction,
	getGlobalUndefinedCountAction,
	getDeteriorationModelStatus,
} from '@Actions';
import { CurveMethod, WeibullFormulation } from '@Types';
import { selectDeteriorationModel } from '@Selectors';
import { ALL_CURVE_CONFIGS_SLICE } from './constants';

interface ConfigurationSectionProps {
	curveState: ReturnType<typeof useCurveConfig>;
	industryStandardCurves: {
		curves: { key: string; displayName: string }[];
		isLoading: boolean;
	};
	currentCurveConfigId: string;
	modelId: string;
	failureCount?: number;
	isBulkMode?: boolean;
	hideButtons?: boolean;
	onSaveComplete?: () => void;
}

export const ConfigurationSection = ({
	curveState,
	industryStandardCurves,
	currentCurveConfigId,
	modelId,
	failureCount,
	isBulkMode = false,
	hideButtons = false,
	onSaveComplete,
}: ConfigurationSectionProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const [isSaving, setIsSaving] = useState(false);
	const deteriorationModel = selectDeteriorationModel();
	const weibullFormulation =
		deteriorationModel.deteriorationModel?.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER;
	const isWeibull3ParamEnabled = useIsFeatureEnabled('info360-weibull-3-param');

	// Check if the curve configuration has changed from the original
	const hasChanges = useMemo(() => {
		const originalConfig = curveState.originalSavedConfig;
		if (!originalConfig) return false;

		// If the method changed, there are changes
		if (curveState.selectedCurveMethod !== originalConfig.selectedCurveMethod) {
			return true;
		}

		// Check if the associated data changed based on the method type
		switch (curveState.selectedCurveMethod) {
			case CurveMethod.INDUSTRY_CURVE:
				return curveState.industryStandardCurve !== originalConfig.reference;
			case CurveMethod.COPIED_CURVE:
				return curveState.copyCurve !== originalConfig.reference;
			case CurveMethod.ONE_EXPECTED_LIFE:
				return curveState.oneExpectedUsefulLife !== originalConfig.data;
			case CurveMethod.FAILURE_DEFINITION:
				return (
					curveState.weibullFormulation !== (originalConfig.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER)
				);
			case CurveMethod.CUSTOM_CURVE: {
				// Compare custom curve points
				const originalPoints = originalConfig.data as { age: number; survivalProbability: number }[] | null;
				if (!originalPoints) {
					if (curveState.customCurvePoints.length > 0) return true;
				} else if (curveState.customCurvePoints.length !== originalPoints.length) {
					return true;
				} else if (
					curveState.customCurvePoints.some(
						(point, index) =>
							point.age !== originalPoints[index]?.age ||
							point.survivalProbability !== originalPoints[index]?.survivalProbability,
					)
				) {
					return true;
				}
				return (
					curveState.weibullFormulation !== (originalConfig.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER)
				);
			}
			default:
				return false;
		}
	}, [curveState]);

	const isCustomCurveValid = useMemo(() => {
		if (curveState.selectedCurveMethod !== CurveMethod.CUSTOM_CURVE) {
			return true;
		}
		const validPoints = curveState.customCurvePoints.filter(
			point => point.age !== null && point.survivalProbability !== null,
		);
		return validPoints.length >= 2;
	}, [curveState.selectedCurveMethod, curveState.customCurvePoints]);

	const isIndustryCurveValid =
		curveState.selectedCurveMethod !== CurveMethod.INDUSTRY_CURVE || Boolean(curveState.industryStandardCurve);

	const isApplyDisabled =
		isSaving ||
		curveState.selectedCurveMethod === CurveMethod.UNDEFINED ||
		!hasChanges ||
		!isCustomCurveValid ||
		!isIndustryCurveValid;

	const getSavedMethodDisplayName = (): string | null => {
		const savedMethod = curveState.originalSavedConfig?.selectedCurveMethod;
		if (!savedMethod || savedMethod === CurveMethod.UNDEFINED) return null;

		switch (savedMethod) {
			case CurveMethod.FAILURE_DEFINITION:
				return t('From Failure Definition');
			case CurveMethod.CUSTOM_CURVE:
				return t('Custom Curve');
			case CurveMethod.INDUSTRY_CURVE:
				return t('Industry Standard Curve');
			case CurveMethod.COPIED_CURVE:
				return t('Copy Curve from Another Group');
			case CurveMethod.ONE_EXPECTED_LIFE:
				return t('One Expected Useful Life');
			default:
				return null;
		}
	};

	const savedMethodDisplayName = getSavedMethodDisplayName();

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const curveMethodData = {
				selectedCurveMethod: curveState.selectedCurveMethod,
				industryCurve: curveState.industryStandardCurve,
				customCurve: curveState.customCurvePoints,
				oneExpectedLife: curveState.oneExpectedUsefulLife,
				copiedCurve: curveState.copyCurve,
			};

			await updateModelCurveConfigs({
				modelId,
				weibullFormulation,
				curveConfigs: [
					{
						curveConfigId: currentCurveConfigId,
						selectedCurveMethod: curveMethodData.selectedCurveMethod,
						copiedCurve: curveMethodData.copiedCurve ?? undefined,
						industryCurve: curveMethodData.industryCurve ?? undefined,
						oneExpectedLife: curveMethodData.oneExpectedLife ?? undefined,
						customCurve: curveMethodData.customCurve
							.filter((point): point is { age: number; survivalProbability: number } => point.age !== null)
							.map(point => ({ age: point.age, survivalProbability: point.survivalProbability })),
						// Only send weibullFormulation when the 3P flag is on, so a save with the flag off
						// doesn't overwrite a group previously saved as 3-parameter with the 2P default.
						...(isWeibull3ParamEnabled ? { weibullFormulation: curveState.weibullFormulation } : {}),
					},
				],
			});

			curveState.clearLocalChanges();

			dispatch(
				listDeteriorationModelCurveConfigsAction({
					modelId,
					page: 1,
					slice: ALL_CURVE_CONFIGS_SLICE,
					sortKey: 'assetGroupName',
					sortDescending: true,
					searchValue: null,
					filter: null,
					filterModel: { items: [] },
				}),
			);
			dispatch(getGlobalUndefinedCountAction({ modelId }));
			dispatch(getDeteriorationModelStatus({ id: modelId }));
			dispatch(success(t('Curve method saved successfully')));
			onSaveComplete?.();
		} catch (e) {
			console.error('Error saving curve method:', e);
			dispatch(error(t('Failed to save curve method')));
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Paper
			elevation={0}
			sx={{
				height: rem(600),
				display: 'flex',
				flexDirection: 'column',
				padding: rem(16),
				border: '1px solid #e5e7eb',
				borderRadius: rem(12),
			}}>
			<Text style={{ fontFamily: WEAVE_FONT, fontWeight: 600, fontSize: '1.125rem', marginBottom: rem(16) }}>
				{t('Survival Curve Method for selected Group')}
			</Text>

			<Text style={{ fontFamily: WEAVE_FONT, fontWeight: 500, fontSize: '1rem', marginBottom: rem(16) }}>
				{t('Preview options, then apply your selection.')}
			</Text>

			<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
				<CurveMethodRadioGroup
					curveState={curveState}
					industryStandardCurves={industryStandardCurves}
					currentCurveConfigId={currentCurveConfigId}
					modelId={modelId}
					failureCount={failureCount ?? 0}
					isBulkMode={isBulkMode}
				/>
			</Box>

			{!hideButtons && (
				<Stack
					spacing={rem(12)}
					sx={{
						borderTop: rem(1),
						borderColor: 'divider',
						paddingTop: rem(8),
						marginTop: rem(8),
					}}>
					{savedMethodDisplayName && (
						<Text style={{ opacity: 0.6 }}>{`${t('Current saved method:')} ${savedMethodDisplayName}`}</Text>
					)}
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={rem(8)}
						sx={{
							...(savedMethodDisplayName && {
								borderTop: '1px solid rgba(0, 0, 0, 0.08)',
								paddingTop: rem(12),
							}),
						}}>
						<Button variant="contained" color="secondary" onClick={curveState.cancelChanges} disabled={isSaving}>
							{t('Cancel')}
						</Button>
						<Button variant="contained" color="primary" onClick={handleSave} disabled={isApplyDisabled}>
							{isSaving ? t('Saving...') : t('Apply Selected Method to Group')}
						</Button>
					</Stack>
				</Stack>
			)}
		</Paper>
	);
};
